import _extends from "@babel/runtime/helpers/extends";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Badge, Box, Button, Grid, Icon, InputCheckbox, Link, Picture, Text } from "~/components-shared";
import { AuthoritiesEnum, RoutesEnum } from "~/types/enums";
import { useHistory } from "react-router-dom";
import moment from "moment";
import styled, { useTheme } from "styled-components";
import lists from "~/lib/lists";
import useAbacExt from "~/hooks/abac";
import TopicFootprintContent from "~/components/TopicFootprintContent";
import Markdown from "~/components/Markdown";
import { useSelector } from "react-redux";
import authSelectors from "~/store/auth/selectors";
import ErrorBoundaryCard from "../ErrorBoundaryCard";
const Timeline = styled.div`
  position: relative;
  width: 6px;
  height: 100%;
  cursor: pointer;

  &:before {
    content: "";
    position: absolute;
    width: 2.4rem;
    height: 2.4rem;
    background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.primary;
}};
    border-radius: 50%;
    left: -1rem;
    top: 0.35rem;
    z-index: 1;
  }

  &:after {
    content: "";
    position: absolute;
    width: 2px;
    height: 10000px;
    background-color: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.borders.primary;
}};
    right: 2px;
    top: 2rem;
    margin-top: -2px;
  }
`;
const SourcePicture = styled(Picture)`
  position: absolute;
  top: 0.8rem;
  left: calc(3px - 0.75rem);
  z-index: 1;
`;
const StyledBox = styled(Box)`
  position: absolute;
  top: 1rem;
  right: 1rem;
`;
const StyledButton = styled(Button)`
  & span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
const Analysis = styled.div`
  padding-left: 1em;
  position: relative;

  &:before {
    position: absolute;
    content: "";
    left: 0;
    top: 0;
    width: 3px;
    height: 100%;
    background-color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.primary.main;
}};
    border-radius: 3px;
  }
`;
const Content = styled.div`
  overflow: hidden;
  max-width: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.spacing.step * 10;
}}rem;
`;
const Footprint = _ref5 => {
  let {
    footprint,
    showExplanation,
    selection,
    setSelection,
    hideActions = false,
    ...rest
  } = _ref5;
  const isAllSelected = selection.includes("*");
  const isIncluded = selection.includes(footprint.id);
  const isSelected = !isAllSelected && isIncluded || isAllSelected && !isIncluded;
  const theme = useTheme();
  const user = useSelector(authSelectors.getUser);
  const {
    t
  } = useTranslation();
  const history = useHistory();
  const {
    userHas
  } = useAbacExt();
  const [isHovered, setIsHovered] = useState(false);
  const handleMember = id => {
    history.push(user.id === id ? RoutesEnum.PROFILE : RoutesEnum.MEMBER.replace(":memberId", id));
  };
  const handleTopicClick = topicId => {
    const url = `${window.location.origin}${RoutesEnum.TOPIC.replace(":topicId", topicId)}?view=footprints`;
    window.open(url, "_blank");
  };
  return /*#__PURE__*/React.createElement(Grid, _extends({
    className: `topic__footprint${isSelected ? " topic__footprint--selected" : ""}${isHovered ? " topic__footprint--hovered" : ""}`,
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    style: {
      position: "relative"
    },
    cols: 4,
    gap: 6,
    template: `${hideActions ? "160px" : "200px"} 6px 2.5rem 1fr`,
    fullWidth: true
  }, rest), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: !hideActions && userHas(AuthoritiesEnum.FOOTPRINTS_WRITE) ? "40px 1fr" : "1fr"
  }, !hideActions && userHas(AuthoritiesEnum.FOOTPRINTS_WRITE) && /*#__PURE__*/React.createElement(InputCheckbox, {
    value: isSelected,
    onChange: () => setSelection([footprint.id]),
    className: "topic__footprint-checkbox",
    s: {
      ml: 4,
      mt: 1
    },
    color: "primary"
  }), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    align: "right",
    s: {
      mt: 3,
      pl: 5
    }
  }, moment(footprint.date).local().startOf("seconds").fromNow(true), " ", t("Activities.ago"), /*#__PURE__*/React.createElement(Text, {
    className: "topic__footprint-absolute-date",
    color: "textSecondary",
    display: "block"
  }, moment(footprint.date).format("ddd, MMM D hh:mma")))), /*#__PURE__*/React.createElement(Timeline, {
    className: "topic__footprint-timeline",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-float": true,
    "data-tooltip-content": lists.common.apps.names[footprint.source]
  }, /*#__PURE__*/React.createElement(SourcePicture, {
    border: "squared",
    src: lists.common.apps.logos(theme)[footprint.source],
    width: "1.5rem",
    height: "1.5rem",
    color: "backgroundSecondary",
    backgroundSize: "contain"
  })), /*#__PURE__*/React.createElement(Avatar, {
    size: 11,
    user: footprint.createdBy
  }), /*#__PURE__*/React.createElement(Grid.Cell, {
    s: {
      mb: 6
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 2,
    template: "1fr auto",
    s: {
      mr: 2
    }
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Link, {
    weight: "semiBold",
    color: theme.colors.text.primary,
    onClick: () => handleMember(footprint.createdBy.id),
    underline: true
  }, footprint.createdBy.firstName, " ", footprint.createdBy.lastName), /*#__PURE__*/React.createElement(Content, {
    className: "search-content"
  }, /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(TopicFootprintContent, {
    footprint: footprint
  }))), showExplanation && footprint.aiAnalysis && /*#__PURE__*/React.createElement(Analysis, null, /*#__PURE__*/React.createElement(Markdown, {
    text: `**Explanation**: ${footprint.aiAnalysis.explanation || "-"}\n**Confidence level**: ${footprint.aiAnalysis.confidenceLevel || "-"}\n**Intentions**: ${footprint.aiAnalysis.intentions ? footprint.aiAnalysis.intentions?.join(", ") : "-"}\n**Keywords**: ${footprint.aiAnalysis.keywords ? footprint.aiAnalysis.keywords?.join(", ") : "-"}\n**Labels**: ${footprint.aiAnalysis.labels ? footprint.aiAnalysis.labels?.join(", ") : "-"}\n**Topic**: ${footprint.aiAnalysis.topic ? footprint.aiAnalysis.topic : "-"}\n**Summary**: ${footprint.aiAnalysis.summary ? footprint.aiAnalysis.summary : "-"}\n**Changes**: ${footprint.aiAnalysis.changes ? footprint.aiAnalysis.changes : "-"}`
  })))), /*#__PURE__*/React.createElement(StyledBox, {
    gap: 1
  }, footprint.thresholdNotReached && /*#__PURE__*/React.createElement(Badge, {
    color: "translucentDanger"
  }, "Threshold not reached"), footprint.topics.length ? /*#__PURE__*/React.createElement(Box, {
    gap: 1,
    direction: "column"
  }, footprint.topics.map(topic => /*#__PURE__*/React.createElement(StyledButton, {
    key: topic.id,
    onClick: () => handleTopicClick(topic.id),
    fontSize: 3,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": topic.name,
    s: {
      px: 2,
      py: 1
    },
    maxWidth: "200px",
    color: "translucentPrimary"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "timelapse",
    s: {
      mr: 1,
      mt: -0.5
    }
  }), topic.name))) : /*#__PURE__*/React.createElement(Badge, {
    color: "translucent"
  }, "Not Mapped"))));
};
export default Footprint;